import React, { useEffect, useRef, useState } from 'react';

// Inline films for the product pages (media/platform-film, app-kit-film,
// gate-film, pricing-film) and the muted module loops
// (media/module-shorts/<slug>). Dormant until the matching version is set
// at build time — /media/** caches for a week and the file names never
// change, so the query string is the cache-bust. The bridge film keeps
// REACT_APP_FILM_VERSION; these are separate so a page-film recut does not
// pretend the bridge changed.
const PAGE_VERSION = process.env.REACT_APP_PAGE_FILM_VERSION || '';
const MODULE_VERSION = process.env.REACT_APP_MODULE_FILM_VERSION || '';
export const pageFilmsEnabled = () => Boolean(PAGE_VERSION);
export const moduleFilmsEnabled = () => Boolean(MODULE_VERSION);

const file = (base, name, version) => `${base}/${name}?v=${version}`;

// Sound-on player. `chapters` is [{ at, title }]. `hashSeek` seeks when the
// page opens on that hash (the /gate#routing proof link).
export function PageFilm({
  base, prefix, title, summary, chapters = [], hashSeek = null,
}) {
  const mobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 719px)').matches;
  const ref = useRef(null);
  const [t, setT] = useState(0);
  const version = PAGE_VERSION;
  if (!version) return null;
  const src = (name) => file(base, name, version);
  const go = (at) => {
    const v = ref.current;
    if (!v) return;
    v.currentTime = at;
    setT(at);
    v.play().catch(() => {});
  };
  return (
    <figure className="cs-page-film" id={`${prefix}-film`}>
      <FilmSurface
        videoRef={ref}
        mobile={mobile}
        poster={src(`${prefix}-poster.jpg`)}
        webm={src(`${prefix}-1080.webm`)}
        mp4={src(`${prefix}-1080.mp4`)}
        mobileSrc={src(`${prefix}-720-mobile.mp4`)}
        captions={src(`${prefix}-captions.vtt`)}
        title={title}
        hashSeek={hashSeek}
        onTime={setT}
      />
      {chapters.length > 0 && (
        <div className="cs-film-chapters" role="tablist" aria-label="Chapters">
          {chapters.map((ch, i) => (
            <button key={ch.title} type="button" role="tab" className={t >= ch.at - 0.4 && (i === chapters.length - 1 || t < chapters[i + 1].at - 0.4) ? 'is-current' : ''} onClick={() => go(ch.at)}>
              <span>{i + 1}</span>{ch.title}
            </button>
          ))}
        </div>
      )}
      <figcaption>{summary}</figcaption>
    </figure>
  );
}

function FilmSurface({ videoRef, mobile, poster, webm, mp4, mobileSrc, captions, title, hashSeek, onTime }) {
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !hashSeek || window.location.hash !== hashSeek.hash) return undefined;
    const at = hashSeek.at;
    const seek = () => {
      v.currentTime = at;
      v.play().catch(() => {});
      document.getElementById(hashSeek.scrollTo)?.scrollIntoView({ block: 'start' });
    };
    if (v.readyState >= 1) seek();
    else v.addEventListener('loadedmetadata', seek, { once: true });
    return () => v.removeEventListener('loadedmetadata', seek);
  }, [videoRef, hashSeek]);
  return (
    <video
      ref={videoRef}
      className="cs-results-film"
      controls
      playsInline
      preload="metadata"
      poster={poster}
      crossOrigin="anonymous"
      aria-label={title}
      onTimeUpdate={(e) => onTime(e.currentTarget.currentTime)}
    >
      {!mobile && <source src={webm} type="video/webm" />}
      {!mobile && <source src={mp4} type="video/mp4" />}
      {mobile && <source src={mobileSrc} type="video/mp4" />}
      <track kind="captions" src={captions} srcLang="en" label="English" default />
    </video>
  );
}

// Module pages that have a shipped short. agent-io and nventr-agent share one.
export const MODULE_FILM = {
  nqube: 'nqube',
  agentos: 'agentos',
  'dev-console': 'dev-console',
  'agent-io': 'agent-io',
  'nventr-agent': 'agent-io',
  athena: 'athena',
  devops: 'devops',
  listen: 'listen',
};

// Muted loop in the module hero, poster first, reduced-motion stays on the
// poster. The narrated file is what plays; the element is muted until the
// visitor presses "Sound on", which restarts the short from the top with
// the voice and the captions track, then returns to the muted loop at the end.
export function ModuleFilm({ slug, alt }) {
  const id = MODULE_FILM[slug];
  const [cut, setCut] = useState(null);
  const [sound, setSound] = useState(false);
  const videoRef = useRef(null);
  const version = MODULE_VERSION;
  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    if (sound) {
      el.muted = true;
      el.loop = true;
      setSound(false);
      el.play().catch(() => {});
      return;
    }
    el.muted = false;
    el.loop = false;
    el.currentTime = 0;
    setSound(true);
    el.play().catch(() => setSound(false));
  };
  const onEnded = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    el.loop = true;
    setSound(false);
    el.play().catch(() => {});
  };
  useEffect(() => {
    if (!version || !id) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    if (reduce || saveData) { setCut('poster'); return undefined; }
    const pick = () => setCut(window.matchMedia('(max-width: 719px)').matches ? 'mobile' : 'desktop');
    const tid = window.setTimeout(pick, 200);
    return () => window.clearTimeout(tid);
  }, [version, id]);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    const play = el.play();
    if (play && play.catch) play.catch(() => setCut('poster'));
  }, [cut]);
  if (!version || !id) return null;
  const src = (name) => file(`/media/module-shorts`, name, version);
  return (
    <div className={`cs-mod-film ${cut === 'desktop' || cut === 'mobile' ? 'is-ready' : ''}`}>
      <img src={src(`${id}-poster.jpg`)} alt={alt} decoding="async" />
      {(cut === 'desktop' || cut === 'mobile') && (
        <video
          key={cut}
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          crossOrigin="anonymous"
          aria-hidden={sound ? undefined : 'true'}
          aria-label={sound ? alt : undefined}
          onEnded={onEnded}
          onError={() => setCut('poster')}
        >
          {cut === 'desktop' && <source src={src(`${id}-1080.webm`)} type="video/webm" />}
          {cut === 'desktop' && <source src={src(`${id}-1080.mp4`)} type="video/mp4" />}
          {cut === 'mobile' && <source src={src(`${id}-720-mobile.mp4`)} type="video/mp4" />}
          {/* The words are burned into the picture (compose.py burnCaptions); the track is for assistive tech. */}
          <track kind="captions" src={src(`${id}-captions.vtt`)} srcLang="en" label="English" />
        </video>
      )}
      {(cut === 'desktop' || cut === 'mobile') && (
        <button type="button" className="cs-mod-film-sound" onClick={toggleSound} aria-pressed={sound} data-nova-note="Plays the module short with its narration">
          {sound ? 'Sound off' : 'Sound on'}
        </button>
      )}
    </div>
  );
}
